import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import Filter from "../filter/Filter";
import { Select, MenuItem, Tooltip } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Export from "../export/Export";
import IconButton from "@mui/material/IconButton";
import { useToastContext } from "../../context/toast";
import { DASHBOARD_CUSTOMER_CONSTANTS } from "../../constants/CustomerConstants";
import moment from "moment";
import ConfigColumn from "../config-column/config-column";
import { GearIcon } from "../Reusables/icons";
import { useUserContext } from "../../context/UserContext";

const Search = ({
	onApplyFilters,
	onExportButtonClick,
	onCopyToClipboard,
	isCopyDisabled,
	shipmentCount,
	onConfigColClicked,
	configColumnDailogOpen,
	setIsConfigColumnDailogOpen,
	columns,
	handleApplyColumns,
	exportPopupOpen,
	onExportPopupClose,
	appliedFiltersCount,
	appliedFilters,
	isExportDisabled,
	onAvailableDatesChanged,
	accountInfo,
}) => {
	const theme = useTheme();
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchType, setSearchType] = useState("");
	const [isInvalidSearchType, setIsInvalidSearchType] = useState(false);
	const { showErrorToast } = useToastContext();
	const [isColConfigHover, setIsColConfigHover] = useState(false);
	const [copyFeedback, setCopyFeedback] = useState(null);
	const [isCopying, setIsCopying] = useState(false);
	const { userDetails } = useUserContext();
	const isCustomerUser = userDetails.isCustomerUser === true;
	const customerName = userDetails.customerName;

	const getCopyTooltip = () => {
		if (copyFeedback) return copyFeedback;
		if (isCopying) return "Copying...";
		if (shipmentCount > 0) {
			return `Copy ${shipmentCount.toLocaleString()} shipment${shipmentCount !== 1 ? 's' : ''} to clipboard`;
		}
		return "Copy to clipboard";
	};

	const handleCopyClick = async () => {
		if (onCopyToClipboard) {
			setIsCopying(true);
			const success = await onCopyToClipboard();
			setIsCopying(false);
			if (success) {
				setCopyFeedback("Copied!");
				setTimeout(() => setCopyFeedback(null), 2000);
			} else {
				setCopyFeedback("Failed to copy");
				setTimeout(() => setCopyFeedback(null), 2000);
			}
		}
	};

	// Function to get user-friendly search type label
	const getSearchTypeLabel = (type) => {
		switch (type) {
			case "PONumber":
				return "PO Number";
			case "DeliveryNumber":
				return "Delivery Number";
			case "NBNumber":
				return "NB Number";
			default:
				return "";
		}
	};

	const handleSearchTypeChange = (event) => {
		const searchType = event.target.value;
		setSearchType(searchType === "" ? null : searchType);
		setSearchQuery("");
		setIsInvalidSearchType(false);
		if (searchType === "") {
			const updatedFilters = {
				...appliedFilters,
				SearchQuery: "",
			};
			// Remove any search-specific filters
			delete updatedFilters.PONumber;
			delete updatedFilters.DeliveryNumber;
			delete updatedFilters.NBNumber;
			// Maintain customer name for customer users
			if (isCustomerUser && customerName) {
				updatedFilters.CustomerName = customerName;
			}

			updatedFilters.AppliedFiltersCount = appliedFilters.AppliedFiltersCount || 1;

			onApplyFilters(updatedFilters);
		}
	};

	const handleSearchQueryChange = (event) => {
		setSearchQuery(event.target.value);
	};

	const handleSearch = () => {
		if (searchType === "" || searchType === null) {
			showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.INVALID_SEARCH_TYPE);
			setSearchQuery("");
			setIsInvalidSearchType(true);

			setTimeout(() => {
				setIsInvalidSearchType(false);
			}, 2500);

			return;
		}

		const searchFilters = {
			SearchQuery: searchType ? `${searchType}:${searchQuery}` : "",
		};

		if (isCustomerUser && customerName) {
			searchFilters.CustomerName = customerName;
		}
		searchFilters.AppliedFiltersCount = appliedFilters.AppliedFiltersCount || 1;
		onApplyFilters(searchFilters, true);
	};

	const handleClearSearchFilters = () => {
		setSearchQuery("");
		setSearchType("");
		setIsInvalidSearchType(false);

		const filters = {
			...appliedFilters,
			SearchQuery: "",
			AppliedFiltersCount: appliedFilters.AppliedFiltersCount || 1,
			CustomerName: isCustomerUser ? customerName : appliedFilters.CustomerName,
		};

		// Add CustomerName only if it's a customer user and we have a name
		if (isCustomerUser && customerName) {
			filters.CustomerName = customerName;
		}

		onApplyFilters(filters);
	};

	function handleApplyFilters(filters) {
		setSearchQuery("");
		setSearchType("");
		setIsInvalidSearchType(false);
		onApplyFilters(filters);
	}
	const handleClearFilters = () => {
		setSearchQuery("");
		setSearchType("");
		setIsInvalidSearchType(false);
		onApplyFilters(getDefaultFilters());
	};

	const getDefaultFilters = () => {
		const currentDate = new Date();
		const filters = {
			SelectedDate: moment(currentDate).format("YYYYMMDD"),
			CustomerName: isCustomerUser ? customerName : null,
			CustomerRegion: null,
			DeliveryStatus: null,
			City: null,
			Warehouse: null,
			AppliedFiltersCount: 1,
			isCustomerUser: isCustomerUser,
			reschedule: false,
			autoRecovery: false,
		};
		return filters;
	};

	const toggleSidebar = () => {
		setIsFilterModalOpen((prevState) => !prevState);
	};

	return (
		<Box
			sx={{
				padding: "0px 20px",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				background: theme.palette.background.default,
				color: theme.palette.text.primary,
				height: "40px",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
				<Select
					value={searchType || ""}
					onChange={handleSearchTypeChange}
					displayEmpty
					renderValue={(selected) =>
						selected ? getSearchTypeLabel(selected) : "Select Search Type"
					}
					sx={{
						fontFamily: "Poppins, sans-serif",
						padding: "10px",
						height: "40px",
						borderRadius: "4px",
						color: theme.palette.text.primary,
						fontSize: "1rem",
						"& fieldset": {
							border: isInvalidSearchType ? `1px solid red` : `${theme.palette.border.main}`, // Conditional border color,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.border.main,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.FocusStyle.main,
						},
					}}
				>
					<MenuItem value="PONumber" sx={{ fontSize: "1rem" }}>
						PO Number
					</MenuItem>
					<MenuItem value="DeliveryNumber" sx={{ fontSize: "1rem" }}>
						Delivery Number
					</MenuItem>
					{!isCustomerUser && (
						<MenuItem value="NBNumber" sx={{ fontSize: "1rem" }}>
							NB Number
						</MenuItem>
					)}
					<MenuItem value="" onClick={handleClearSearchFilters} sx={{ fontSize: "1rem" }}>
						Clear
					</MenuItem>
				</Select>
				<OutlinedInput
					placeholder={
						searchType
							? `Search by ${getSearchTypeLabel(searchType)}`
							: isCustomerUser
								? "Search by PO/Delivery Number"
								: "Search by PO/Delivery/NB Number"
					}
					type="text"
					value={searchQuery}
					onChange={handleSearchQueryChange}
					sx={{
						fontFamily: "Poppins, sans-serif",
						padding: "10px",
						width: "500px",
						height: "40px",
						borderRadius: "4px",
						fontSize: "15px",
						color: theme.palette.text.primary,
						"& fieldset": {
							border: theme.palette.border.main,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.border.main,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.FocusStyle.main,
						},
					}}
					endAdornment={
						<InputAdornment position="end">
							<IconButton onClick={handleSearch}>
								<SearchIcon sx={{ color: theme.palette.text.primary }} />
							</IconButton>
						</InputAdornment>
					}
				/>
				<Button
					variant="contained"
					onClick={toggleSidebar}
					startIcon={<FilterAltOutlinedIcon />}
					endIcon={
						appliedFiltersCount > 0 && (
							<Box
								sx={{
									backgroundColor: "#FFFFFF",
									color: theme.palette.button.main,
									borderRadius: "50%",
									width: "20px",
									height: "20px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "12px",
									marginLeft: "8px",
									"& > span": {
										transform: "scale(0.8)",
									},
								}}
							>
								<span>{appliedFiltersCount}</span>
							</Box>
						)
					}
					sx={{
						textTransform: "none",
						fontFamily: "Poppins, sans-serif",
						color: theme.palette.text.buttonhover,
						backgroundColor: theme.palette.button.main,
						height: "40px",
						"&:hover": {
							backgroundColor: theme.palette.buttonhover.main,
						},
					}}
				>
					Filters
				</Button>
			</Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "end",
					alignItems: "center",
					width: "100%",
					gap: 1,
				}}
			>
				<Tooltip title={getCopyTooltip()}>
					<span>
						<Button
							variant="outlined"
							disabled={isCopyDisabled || isCopying}
							onClick={handleCopyClick}
							startIcon={<ContentCopyIcon />}
							sx={{
								fontFamily: "Poppins, sans-serif",
								color: theme.palette.text.button,
								borderColor: theme.palette.border.main,
								"&:hover": {
									backgroundColor: theme.palette.buttonhover.main,
									color: theme.palette.text.buttonhover,
									borderColor: theme.palette.buttonhover.main,
								},
							}}
						>
							{isCopying ? "Copying..." : "Copy"}
						</Button>
					</span>
				</Tooltip>
				<Button
					variant="outlined"
					disabled={isExportDisabled}
					onClick={onExportButtonClick}
					startIcon={<IosShareIcon />}
					sx={{
						fontFamily: "Poppins, sans-serif",
						color: theme.palette.text.button,
						borderColor: theme.palette.border.main,
						"&:hover": {
							backgroundColor: theme.palette.buttonhover.main,
							color: theme.palette.text.buttonhover,
							borderColor: theme.palette.buttonhover.main,
						},
					}}
				>
					Export
				</Button>

				{!isCustomerUser && (
					<Button
						variant="outlined"
						onMouseEnter={() => setIsColConfigHover(true)}
						onMouseLeave={() => setIsColConfigHover(false)}
						onClick={onConfigColClicked}
						startIcon={
							<GearIcon
								fill={isColConfigHover ? theme.palette.text.buttonhover : theme.palette.text.button}
							/>
						}
						sx={{
							fontFamily: "Poppins, sans-serif",
							color: theme.palette.text.button,
							borderColor: theme.palette.border.main,
							"&:hover": {
								backgroundColor: theme.palette.buttonhover.main,
								color: theme.palette.text.buttonhover,
								borderColor: theme.palette.buttonhover.main,
							},
						}}
					>
						Customize Column
					</Button>
				)}
			</Box>
			<Export open={exportPopupOpen} handleClose={onExportPopupClose} />
			{!isCustomerUser && configColumnDailogOpen && (
				<ConfigColumn
					columns={JSON.parse(JSON.stringify(columns ? columns : []))}
					open={configColumnDailogOpen}
					handleApplyColumns={handleApplyColumns}
					handleClose={() => setIsConfigColumnDailogOpen(false)}
				/>
			)}
			<Filter
				isOpen={isFilterModalOpen}
				toggleDrawer={toggleSidebar}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearFilters}
				data-testid="Filter"
				appliedFiltersCount={appliedFiltersCount}
				accountInfo={accountInfo}
				currentFilters={appliedFilters}
				onAvailableDatesChanged={onAvailableDatesChanged}
			/>
		</Box>
	);
};

export default Search;
